// NeedsLinking — lists published car listings with no canonical catalog entry
// (cars.car_id IS NULL) and lets an operator pick a Make → Model → Entry from
// the vehicle-catalog hierarchy and assign it via POST /admin/vehicle-catalog/link.
//
// Mirrors VehicleCatalog.tsx conventions: same data-fetch/auth client, same
// make→model→entry cascade helpers, same component/styling idioms, same
// loading/error handling patterns.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PageHeader, TablePager } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchCatalogMakes,
  fetchCatalogModels,
  fetchCatalogEntries,
  type CatalogMakeRow,
  type CatalogModelRow,
  type CatalogEntryListRow,
} from '@/lib/admin-api';
import { api } from '@/lib/api';
import { usePager } from '@/lib/use-pager';

// ─── API shapes ───────────────────────────────────────────────────────

export interface NeedsLinkingRow {
  id: number;
  title: string | null;
  vendorId: number | null;
  makeSlug: string | null;
  modelSlug: string | null;
}

interface NeedsLinkingResponse {
  listings: NeedsLinkingRow[];
  total: number;
  page: number;
  limit: number;
}

function fetchNeedsLinking(
  params: { page: number; limit: number },
  signal?: AbortSignal,
): Promise<NeedsLinkingResponse> {
  return api<NeedsLinkingResponse>({
    path: `/admin/vehicle-catalog/needs-linking?page=${params.page}&limit=${params.limit}`,
    signal,
  });
}

function linkCatalogEntry(carId: number, entryId: number): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: '/admin/vehicle-catalog/link',
    method: 'POST',
    body: { carId, entryId },
  });
}

// ─── Root component ──────────────────────────────────────────────────

export function NeedsLinking() {
  const { page, limit, pagination } = usePager();

  const listingsQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'needs-linking', page, limit],
    queryFn: ({ signal }) => fetchNeedsLinking({ page, limit }, signal),
  });

  // Local state: remove a row after a successful assignment.
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const listings = (listingsQ.data?.listings ?? []).filter(
    (l) => !dismissed.has(l.id),
  );
  const total = listingsQ.data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Vehicle Catalog"
        title="Needs linking"
        sub="Published listings that have no catalog entry assigned. Pick a Make, Model, and Entry for each row and click Assign to link them."
      />

      {listingsQ.error ? (
        <ErrorPanel message={(listingsQ.error as Error).message} />
      ) : null}

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-4">
        {listingsQ.isLoading ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">Loading listings…</div>
        ) : listings.length === 0 && !listingsQ.isLoading ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">
            {listingsQ.data
              ? 'All listings are linked — nothing left to assign.'
              : 'No data.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-[color:var(--bo-line)] text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  <th className="pb-1.5 pe-3 text-start font-medium">ID</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Title</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Vendor</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Make slug</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Model slug</th>
                  <th className="pb-1.5 text-start font-medium">Assign catalog entry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--bo-line)]">
                {listings.map((row) => (
                  <ListingRow
                    key={row.id}
                    row={row}
                    onAssigned={() =>
                      setDismissed((prev) => new Set([...prev, row.id]))
                    }
                  />
                ))}
              </tbody>
            </table>
            {/* Custom table, not a DataTable — the same pager control, used
                directly, so the two screens cannot drift. */}
            {total > 0 ? (
              <div className="-mx-4 -mb-4 mt-3 border-t border-[color:var(--bo-line)]">
                <TablePager {...pagination(total)} />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Per-listing row with inline make→model→entry picker ─────────────

function ListingRow({
  row,
  onAssigned,
}: {
  row: NeedsLinkingRow;
  onAssigned: () => void;
}) {
  const [selectedMakeId, setSelectedMakeId] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);

  const makesQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'makes'],
    queryFn: ({ signal }) => fetchCatalogMakes(signal),
    staleTime: 60_000,
  });

  const modelsQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'models', selectedMakeId],
    queryFn: ({ signal }) => fetchCatalogModels(selectedMakeId!, signal),
    enabled: selectedMakeId != null,
    staleTime: 60_000,
  });

  const entriesQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'entries', selectedMakeId, selectedModelId],
    queryFn: ({ signal }) =>
      fetchCatalogEntries(
        { makeId: selectedMakeId ?? undefined, modelId: selectedModelId ?? undefined },
        signal,
      ),
    enabled: selectedModelId != null,
    staleTime: 60_000,
  });

  const assignMut = useMutation({
    mutationFn: () => linkCatalogEntry(row.id, selectedEntryId!),
    onSuccess: onAssigned,
  });

  const makes = makesQ.data?.rows ?? [];
  const models = modelsQ.data?.rows ?? [];
  const entries = entriesQ.data?.rows ?? [];

  return (
    <tr className="group align-top">
      <td className="py-2.5 pe-3 tabular-nums text-[color:var(--bo-mute)]">
        {row.id}
      </td>
      <td className="py-2.5 pe-3 font-medium text-[color:var(--bo-ink)]">
        {row.title ?? <span className="text-[color:var(--bo-mute)]">—</span>}
      </td>
      <td className="py-2.5 pe-3 tabular-nums text-[color:var(--bo-mute)]">
        {row.vendorId ?? '—'}
      </td>
      <td className="py-2.5 pe-3 font-mono text-[11.5px] text-[color:var(--bo-mute)]">
        {row.makeSlug ?? '—'}
      </td>
      <td className="py-2.5 pe-3 font-mono text-[11.5px] text-[color:var(--bo-mute)]">
        {row.modelSlug ?? '—'}
      </td>
      <td className="py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {/* Make selector */}
          <MakeSelect
            makes={makes}
            loading={makesQ.isLoading}
            value={selectedMakeId}
            onChange={(id) => {
              setSelectedMakeId(id);
              setSelectedModelId(null);
              setSelectedEntryId(null);
            }}
          />

          {/* Model selector — shown once a make is picked */}
          {selectedMakeId != null && (
            <ModelSelect
              models={models}
              loading={modelsQ.isLoading}
              value={selectedModelId}
              onChange={(id) => {
                setSelectedModelId(id);
                setSelectedEntryId(null);
              }}
            />
          )}

          {/* Entry selector — shown once a model is picked */}
          {selectedModelId != null && (
            <EntrySelect
              entries={entries}
              loading={entriesQ.isLoading}
              value={selectedEntryId}
              onChange={setSelectedEntryId}
            />
          )}

          {/* Assign button — active once an entry is selected */}
          {selectedEntryId != null && (
            <RowAction
              kind="primary"
              disabled={assignMut.isPending}
              busy={assignMut.isPending}
              onClick={() => assignMut.mutate()}
            >
              Assign
            </RowAction>
          )}

          {assignMut.error ? (
            <span className="text-[11px] text-[#9f1239]">
              {(assignMut.error as Error).message}
            </span>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// ─── Cascade select primitives ────────────────────────────────────────

function MakeSelect({
  makes,
  loading,
  value,
  onChange,
}: {
  makes: CatalogMakeRow[];
  loading: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      disabled={loading}
      className="h-8 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] disabled:opacity-60"
    >
      <option value="">
        {loading ? 'Loading makes…' : 'Make…'}
      </option>
      {makes.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

function ModelSelect({
  models,
  loading,
  value,
  onChange,
}: {
  models: CatalogModelRow[];
  loading: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      disabled={loading}
      className="h-8 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] disabled:opacity-60"
    >
      <option value="">
        {loading ? 'Loading models…' : 'Model…'}
      </option>
      {models.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

function EntrySelect({
  entries,
  loading,
  value,
  onChange,
}: {
  entries: CatalogEntryListRow[];
  loading: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      disabled={loading}
      className="h-8 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] disabled:opacity-60"
    >
      <option value="">
        {loading ? 'Loading entries…' : 'Entry…'}
      </option>
      {entries.map((e) => (
        <option key={e.id} value={e.id}>
          {e.defaultTitle} ({e.year}
          {e.trim ? ` · ${e.trim}` : ''})
        </option>
      ))}
    </select>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────

function ErrorPanel({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
    >
      {message}
    </div>
  );
}
